import React, { Component } from 'react';
import { bool, func, object, number, array, string, oneOfType } from 'prop-types';
import { connect } from 'react-redux';
import SlideoutBody from 'ps-components/Slideout/Body/SlideoutBody';
import ViewSwitcher from 'ps-components/ViewSwitcher/ViewSwitcher';
import TableInfoList from 'ps-components/TableInfoList/TableInfoList';
import SMAEmployeeTable from './subcomponents/SMAEmployeeTable';
import Placeholder from 'ps-components/Placeholder/Placeholder';
import ViewGradeSlideoutHeader from './subcomponents/ViewGradeSlideoutHeader';
import ChartsViewGrade from './subcomponents/ChartsViewGrade';
import JobsInGrade from './JobsInGrade';
import {
  backToList,
  clickGrade,
  getEmployees,
  saveState,
  setSearchText,
  setCurrentTab
} from './actions/ViewGradeActions';
import { jobsInRangeSearchActions } from './facets/JobsInRange';
import { GRADE_TABS } from './reducers/ViewGradeReducer';
import { applyRounding, parseNumCheckNull } from 'ps-components/lib/formatters';
import { MODEL_TYPE } from './reducers/GradeListReducer';

import './StructureModelingAnalyticsSlideout.scss';
import './ViewGrade.scss';

export class ViewGrade extends Component {
  static propTypes = {
    hideSlideout: func,
    backToList: func,
    getEmployees: func,
    gradeTitle: string,
    employeeData: array,
    employeeCount: number,
    clickedGrade: object,
    modelId: oneOfType([string, number]),
    smaData: array,
    clickGrade: func,
    modelType: string,
    employeeLoading: bool,
    employeeError: bool,
    handleReset: func,
    typeOfStructure: string,
    setJobIds: func,
    initState: object,
    setSearchText: func,
    searchText: string,
    jobCodeKeyText: string,
    useJobCodeKey: bool,
    saveState: func,
    setCurrentTab: func,
    currentTab: number,
    basis: string,
    basisOptions: array
  };

  state = {
    currentTab: GRADE_TABS.GRADE_DETAILS,
    calledFacetSearch: false // Flag is used to only call facet search on first click of jobs in grade tab
  };

  static defaultProps = {
    clickedGrade: {
      avg: 0,
      current: {
        max: 0,
        mid: 0,
        min: 0
      },
      nextGrade: 0,
      prevGrade: 0,
      proposed: {
        max: 0,
        mid: 0,
        min: 0
      },
      totals: {
        aboveMax: {
          current: 0,
          proposed: 0
        },
        belowMin: {
          current: 0,
          proposed: 0
        },
        costToMin: {
          current: 0,
          proposed: 0
        },
        employeeCount: {
          current: 0,
          proposed: 0
        },
        jobCount: {
          current: 0,
          proposed: 0
        }
      }
    }
  };

  constructor(props) {
    super(props);
    this.viewRef = React.createRef();
    this.formatHeaderData = this.formatHeaderData.bind(this);
    this.formatFooterData = this.formatFooterData.bind(this);
  }

  componentDidMount() {
    // Handles setting slideoutBody height when no footer is present
    const slideoutBody = document.getElementsByClassName('slideout-body')[0];
    if (slideoutBody) {
      slideoutBody.classList.add('no-footer');
    }
  }

  hasJobs(jobsTotal) {
    return jobsTotal && parseInt(jobsTotal) > 0;
  }

  componentDidUpdate(prevProps) {
    if (this.props.gradeTitle !== prevProps.gradeTitle) {
      // Resets and makes facet search call on changing grade
      this.setState({ calledFacetSearch: false });
      return;
    }
    if (!this.state.calledFacetSearch) {
      // calledFacetSearch makes sure when navigating in a grade that unnecessary calls are not made
      // Elastic search/api calls are only made when needed upon initial entry to that grade or switching
      // to a new grade.
      this.setState({ calledFacetSearch: true }, () => {
        const { modelId, gradeTitle, clickedGrade, handleReset, setJobIds } = this.props;
        if (this.hasJobs(clickedGrade.totals.jobCount.proposed)) {
          setJobIds(
            modelId,
            gradeTitle,
            clickedGrade.proposed.min,
            clickedGrade.proposed.mid,
            clickedGrade.proposed.max
          ).then(() => {
            handleReset();
          });
        }
      });
    }
  }

  formatHeaderData() {
    const data = ['Total Employees', '# Below Min', '# Above Max', 'Cost to Move to Min'];
    return data;
  }

  formatFooterData() {
    const { clickedGrade, preferences } = this.props;
    const data = [
      [clickedGrade.totals.employeeCount.current, clickedGrade.totals.employeeCount.proposed],
      [clickedGrade.totals.belowMin.current, clickedGrade.totals.belowMin.proposed],
      [clickedGrade.totals.aboveMax.current, clickedGrade.totals.aboveMax.proposed],
      [clickedGrade.totals.costToMin.current, clickedGrade.totals.costToMin.proposed]
    ];
    return data.map((el, idx) => {
      return (
        <div key={idx}>
          <span className="sma-grade-info-cell-current">
            {idx !== 3
              ? parseNumCheckNull(el[0], true)
              : parseNumCheckNull(applyRounding(el[0], 'monetary', preferences))}
          </span>
          <div className="sma-grade-info-cell-proposed">
            {idx !== 3
              ? parseNumCheckNull(el[1], true)
              : parseNumCheckNull(applyRounding(el[1], 'monetary', preferences))}
          </div>
        </div>
      );
    });
  }

  render() {
    const {
        gradeTitle,
        employeeData,
        clickedGrade,
        modelId,
        smaData,
        employeeCount,
        getEmployees,
        clickGrade,
        employeeLoading,
        employeeError,
        typeOfStructure,
        searchText,
        setSearchText,
        saveState,
        initState,
        jobCodeKeyText,
        useJobCodeKey,
        setCurrentTab,
        currentTab,
        preferences,
        basis,
        basisOptions
      } = this.props,
      isNew = typeOfStructure === MODEL_TYPE.NEW;
    let hasData = true;

    let bodyContent;
    if (currentTab === GRADE_TABS.GRADE_DETAILS) {
      bodyContent = (
        <React.Fragment>
          <ChartsViewGrade clickedGrade={clickedGrade} />
          <div className="sma-grade-info__container">
            <TableInfoList
              tableClass="sma-grade-info__table"
              headerClass="sma-grade-info__header"
              cellClass="sma-grade-info__cell"
              headers={this.formatHeaderData()}
              data={this.formatFooterData()}
            />
            <div className="sma-grade-info__text">
              {!isNew && 'Current'}
              <div className="proposed-text-header">Proposed</div>
            </div>
          </div>
          <SMAEmployeeTable
            viewRef={this.viewRef}
            employeeData={employeeData}
            employeeCount={employeeCount}
            getEmployees={getEmployees}
            modelId={modelId}
            grade={gradeTitle}
            employeeLoading={employeeLoading}
            employeeError={employeeError}
            gradeTotals={clickedGrade.totals}
            saveState={saveState}
            initState={initState}
            preferences={preferences}
            basis={basis}
            basisOptions={basisOptions}
          />
        </React.Fragment>
      );
    } else if (currentTab === GRADE_TABS.GRADE_JOBS) {
      bodyContent = (
        <JobsInGrade
          basis={basis}
          minimum={clickedGrade.proposed.min}
          midpoint={clickedGrade.proposed.mid}
          maximum={clickedGrade.proposed.max}
          setSearchText={setSearchText}
          searchText={searchText}
          useJobCodeKey={useJobCodeKey}
          jobCodeKeyText={jobCodeKeyText}
        />
      );
    }

    let viewSwitchOptions = ['Grade Details', 'Jobs In this Grade'];

    if (this.hasJobs(clickedGrade.totals.jobCount.proposed)) hasData = false;
    return (
      <React.Fragment>
        <ViewGradeSlideoutHeader
          backToList={this.props.backToList}
          hideSlideout={this.props.hideSlideout}
          clickGrade={clickGrade}
          modelId={modelId}
          smaData={smaData}
          scrollRef={this.viewRef}
          prevGradeIdx={clickedGrade.prevGrade}
          nextGradeIdx={clickedGrade.nextGrade}
          currentTab={currentTab}
          basis={basis}
          basisOptions={basisOptions}
        />
        <SlideoutBody ref={this.viewRef}>
          <div className="sma-grade__header-container">
            <h2 className="sma-grade-title">{gradeTitle}</h2>
            <span className="sma-grade__basis">
              Cost is calculated based on {basisOptions.find(({ value }) => value === basis).label}
            </span>
          </div>
          {hasData ? (
            <Placeholder
              className="sma-grade__placeholder pxl-text-muted"
              imagePath="/view/static/mp/images/empty_results.svg"
              secondaryText="No Jobs In This Grade"
              showText={true}
            />
          ) : (
            <React.Fragment>
              <ViewSwitcher
                className="sma-grade__view-switcher"
                isSticky={true}
                tabList={viewSwitchOptions}
                activeTab={currentTab}
                tabSwitch={setCurrentTab}
              />
              {bodyContent}
            </React.Fragment>
          )}
        </SlideoutBody>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ session, structureModeling }) => {
  const { structureModelingAnalytics } = structureModeling;
  let jobCodeKeyText = session.replacementTokens.filter(el => el.token === 'job_code_key_text');
  return {
    gradeTitle: structureModelingAnalytics.ViewGradeReducer.gradeTitle,
    employeeData: structureModelingAnalytics.ViewGradeReducer.employeeData,
    employeeLoading: structureModelingAnalytics.ViewGradeReducer.employeeLoading,
    employeeError: structureModelingAnalytics.ViewGradeReducer.employeeError,
    employeeCount: structureModelingAnalytics.ViewGradeReducer.employeeCount,
    modelId: structureModelingAnalytics.ViewGradeReducer.modelId,
    clickedGrade: structureModelingAnalytics.ViewGradeReducer.clickedGrade,
    offset: structureModelingAnalytics.ViewGradeReducer.offset,
    initState: structureModelingAnalytics.ViewGradeReducer.state,
    searchText: structureModelingAnalytics.ViewGradeReducer.searchText,
    currentTab: structureModelingAnalytics.ViewGradeReducer.currentTab,
    jobCodeKeyText: jobCodeKeyText ? jobCodeKeyText[0].value : 'Job Code Key',
    useJobCodeKey: session.organization.useJobCodeKey
  };
};

const actions = {
  backToList,
  getEmployees,
  clickGrade,
  saveState,
  setSearchText,
  setCurrentTab,
  ...jobsInRangeSearchActions
};

export default connect(mapStateToProps, actions)(ViewGrade);
